import React, { useEffect, useImperativeHandle, useRef, useMemo } from "react";
import clsx from "clsx";
import { Icons, Button, Card, Box } from "@curves/ui-base";
import { isFunction } from "@util/utils";
import { useOrderCart } from "../hooks/useOrderCart";
import { formatCartResult, determineMerchandiseOptions } from "@icoach/accountBook/createOrder/CreateOrderPage.util";
import OrderCartList from "@icoach/accountBook/createOrder/components/OrderCartList";
import CreateOrderDialogBox from "@icoach/accountBook/createOrder/dialog/CreateOrderDialogBox";

/**
 * 新增訂單的主要購物車
 *
 * 主要功能：
 * 1. 管理購物車狀態
 * 2. 提供商品選單彈窗，根據接收者類型顯示不同商品
 * 3. 計算購物車總金額
 * 4. 控制問卷顯示（當有產品類商品時）
 * 5. 處理智能月費同步（當既存月費移除/價格變更時）
 *
 * @param {Object} props - 組件屬性
 * @param {string} props.className - 額外的 CSS 類名
 * @param {Object} props.resource - 資源配置物件
 * @param {Function} props.getReceiver - 取得接收者類型的函數
 * @param {Function} props.setShowQuestionnaireBox - 控制問卷顯示的函數
 * @param {Function} props.updateOrderTotal - 更新訂單總額的函數
 * @param {string} props.memberID - 會員 ID
 * @param {Function} props.checkUsingDeviceOrder - 檢查智能月費的函數
 * @param {Function} [props.wrapUpdatePrice] - 外部包裝的價格更新函數
 * @param {Function} [props.wrapDeleteItem] - 外部包裝的刪除項目函數
 */
const NewOrderCart = React.forwardRef(
	(
		{
			className,
			resource,
			getReceiver,
			setShowQuestionnaireBox,
			updateOrderTotal,
			memberID,
			checkUsingDeviceOrder,
			wrapUpdatePrice: externalWrapUpdatePrice,
			wrapDeleteItem: externalWrapDeleteItem,
		},
		ref,
	) => {
		const dialogRef = useRef(null);

		// 使用 useOrderCart Hook 管理購物車
		const {
			cartItems,
			total: cartTotal,
			setCart,
			clearCart,
			updatePrice: originalUpdatePrice, // 更新價格
			updateQuantity, // 更新數量（僅商品）
			updateNotes, // 更新備註
			deleteItem: originalDeleteItem, // 刪除單個項目
		} = useOrderCart([]);

		// 優先使用外部傳入的包裝方法（來自 CheckoutContent），否則使用預設透傳
		const wrapUpdatePrice = externalWrapUpdatePrice || (fn => fn);
		const wrapDeleteItem = externalWrapDeleteItem || (fn => fn);

		// 包裝後的價格更新和刪除方法（包含智能月費同步邏輯）
		const updatePrice = useMemo(() => wrapUpdatePrice(originalUpdatePrice), [wrapUpdatePrice, originalUpdatePrice]);
		const deleteItem = useMemo(() => wrapDeleteItem(originalDeleteItem), [wrapDeleteItem, originalDeleteItem]);

		// 取得當前接收者類型對應的商品選單
		const receiverMenuOptions = determineMerchandiseOptions(resource, getReceiver);

		// 開啟商品選擇對話框
		const handleClickOpenProductDialog = () => {
			const { openProductSelectionDialog } = dialogRef.current || {};
			isFunction(openProductSelectionDialog) &&
				openProductSelectionDialog(
					{
						menuData: receiverMenuOptions,
						initialCartItems: cartItems,
					},
					setCart,
				);
		};

		// 當購物車資料變更時，更新總額、通知父組件、控制問卷顯示
		useEffect(() => {
			updateOrderTotal();
			const hasProduct = cartItems.some(item => item.hasOwnProperty("productID"));
			setShowQuestionnaireBox(hasProduct);
			// eslint-disable-next-line
		}, [cartItems, updateOrderTotal, setShowQuestionnaireBox]);

		useImperativeHandle(
			ref,
			() => ({
				getResult: () => {
					return formatCartResult(cartItems);
				},
				clearCart: () => clearCart(),
				getTotal: () => cartTotal,
			}),
			[cartItems, cartTotal, clearCart],
		);

		return (
			<Card className={clsx("order-cart", className)}>
				<Card.CardContent>
					{/* 購物車 */}
					<Box className="order-cart-content">
						<OrderCartList
							cartItems={cartItems}
							cartTotal={cartTotal}
							onUpdatePrice={(item, newPrice) => {
								updatePrice(item, newPrice);
							}}
							onUpdateQuantity={updateQuantity}
							onDeleteItem={deleteItem}
							onUpdateNotes={updateNotes}
						/>
					</Box>

					{/* 購物車操作：新增收入項目按鈕 */}
					<Box className="order-cart-actions">
						<Button variant="outlined" onClick={handleClickOpenProductDialog} startIcon={<Icons.CartShopping />}>
							新增商品
						</Button>
					</Box>
					<CreateOrderDialogBox ref={dialogRef} />
				</Card.CardContent>
			</Card>
		);
	},
);

NewOrderCart.displayName = "NewOrderCart";

export default NewOrderCart;
